import { useState, useRef } from 'react';
import { getVehiculos } from '@shared/helpers/api/mantenedores/vehiculos';

/**
 * Hook para búsqueda de vehículos con debounce y AbortController
 * Usado específicamente para dropdowns de selección de patentes/PPU
 */
export const useVehiculosSearch = (user) => {
    const [vehiculos, setVehiculos] = useState([]);
    const [loadingVehiculos, setLoadingVehiculos] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    
    // Referencias para control de timeouts y abortar requests
    const searchTimeout = useRef(null);
    const abortController = useRef(null);

    const searchVehiculos = async (searchTerm) => {
        // Si el término de búsqueda es muy corto, limpiar resultados
        if (!searchTerm || searchTerm.length < 2) {
            setVehiculos([]);
            return;
        }

        // Abortar request anterior si existe
        if (abortController.current) {
            abortController.current.abort();
        }

        // Crear nuevo AbortController
        abortController.current = new AbortController();

        setLoadingVehiculos(true);
        try {
            const response = await getVehiculos(user, {
                searchValue: searchTerm,
                pageSize: 50, // Traer más resultados para la búsqueda
                pageNumber: 1
            });

            // Solo actualizar si el request no fue abortado
            if (!abortController.current?.signal.aborted && response.success) {
                const vehiculosOptions = response.data.map(vehiculo => ({
                    value: vehiculo.ppu,
                    label: vehiculo.ppu || 'Sin PPU',
                    data: vehiculo
                })).filter(option => option.value); // Solo incluir vehículos con PPU

                setVehiculos(vehiculosOptions);
            }
        } catch (error) {
            // Solo mostrar error si no fue abortado
            if (!abortController.current?.signal.aborted) {
                console.error('Error al buscar vehículos:', error);
                setVehiculos([]);
            }
        } finally {
            // Solo actualizar loading si no fue abortado
            if (!abortController.current?.signal.aborted) {
                setLoadingVehiculos(false);
            }
        }
    };

    const handleSearchChange = (inputValue) => {
        setSearchValue(inputValue);

        // Limpiar timeout anterior
        if (searchTimeout.current) {
            clearTimeout(searchTimeout.current);
        }

        // Configurar nuevo timeout para debounce
        searchTimeout.current = setTimeout(() => {
            searchVehiculos(inputValue);
        }, 500);
    };

    const clearSearch = () => {
        setSearchValue('');
        setVehiculos([]);
        
        // Abortar request en curso si existe
        if (abortController.current) {
            abortController.current.abort();
        }
        
        // Limpiar timeout
        if (searchTimeout.current) {
            clearTimeout(searchTimeout.current);
        }
        
        setLoadingVehiculos(false);
    };

    return {
        vehiculos,
        loadingVehiculos,
        searchValue,
        handleSearchChange,
        clearSearch
    };
};
